import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Heart, Moon, Sparkles, Music, Calendar, Percent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_sound-bath-hub/artifacts/013ytm7m_FB_IMG_1770979375236.jpg",
  ambiance: "https://customer-assets.emergentagent.com/job_sound-bath-hub/artifacts/xcrl3xni_FB_IMG_1770979546228.jpg",
};

const benefits = [
  {
    icon: Music,
    title: "Monthly Group Sound Bath",
    description: "One group sound bath each month (in person or live online)",
  },
  {
    icon: Moon,
    title: "Exclusive Meditation",
    description: "One exclusive meditation sent out each month to enjoy in your own time",
  },
  {
    icon: Calendar,
    title: "Priority Access",
    description: "Priority access to events, workshops, and retreats",
  },
  {
    icon: Percent,
    title: "Member Discount",
    description: "10% discount on all private sessions and experiences",
  },
];

const energyPoints = [
  "A space to soften, slow down, and be held",
  "A space where you don't have to be \"on\" all the time",
  "A space where you can breathe, release, and reconnect",
];

export default function MembershipPage() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error("Please enter your name and email");
      return;
    }
    
    setIsSubmitting(true);
    // Simulate waitlist signup
    setTimeout(() => {
      toast.success("You're on the waitlist! We'll notify you when membership launches 💛");
      setEmail("");
      setName("");
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div data-testid="membership-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-end" data-testid="membership-hero">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Sacred Sounds Studio"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
        </div>

        <div className="container-custom relative z-10 pb-24 pt-40">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            {/* Launching Soon Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="inline-block mb-6"
            >
              <span className="bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase px-4 py-2">
                ✨ Launching Soon
              </span>
            </motion.div>
            
            <h1 className="heading-xl mb-6 text-foreground">
              Sacred Sounds Studio<br />
              <em className="italic">Membership</em>
            </h1>
            <p className="body-text text-muted-foreground max-w-lg">
              A monthly space to come home to yourself.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section-spacing bg-muted" data-testid="intro-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="body-text text-muted-foreground text-lg leading-relaxed mb-8">
              Sacred Sounds Studio Membership is a gentle, supportive container designed 
              to help you stay connected to your body, regulate your nervous system, 
              and make time for yourself each month.
            </p>
            <p className="font-serif text-xl text-foreground italic">
              This is for those who are ready to prioritise their wellbeing, 
              not just once in a while, but as a consistent practice.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-spacing" data-testid="benefits-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              What's Included
            </p>
            <h2 className="heading-lg text-foreground">
              Your Monthly <em className="italic">Sanctuary</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="flex gap-6 p-8 bg-muted border border-border"
                data-testid={`benefit-${index}`}
              >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-serif text-xl mb-2 text-foreground">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Energy of This Space */}
      <section className="section-spacing bg-muted" data-testid="energy-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
                The Energy of This Space
              </p>
              <h2 className="heading-lg mb-8 text-foreground">
                This is More Than<br />
                <em className="italic">a Membership</em>
              </h2>
              <p className="body-text text-muted-foreground mb-8">
                It is a space to soften, slow down, and be held.
              </p>
              <ul className="space-y-4">
                {energyPoints.map((point, index) => (
                  <li key={`ep-${point.slice(0,20)}`} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                      <Heart className="w-3 h-3 text-primary" />
                    </div>
                    <span className="body-text text-muted-foreground">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src={IMAGES.ambiance}
                alt="Sound healing atmosphere"
                className="w-full aspect-square object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section-spacing" data-testid="pricing-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-xl mx-auto"
          >
            <div className="bg-muted p-12 border border-border text-center">
              <p className="text-xs tracking-[0.3em] uppercase text-primary mb-6">
                💛 Membership
              </p>
              <div className="mb-6">
                <span className="font-serif text-6xl text-foreground">£25</span>
                <span className="text-muted-foreground ml-2">per month</span>
              </div>
              <ul className="space-y-3 mb-8 text-left max-w-xs mx-auto">
                <li className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-primary" />
                  Rolling monthly membership
                </li>
                <li className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-primary" />
                  Cancel anytime
                </li>
                <li className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-primary" />
                  Switch between in-person or online
                </li>
              </ul>
              
              {/* Coming Soon - Waitlist Form */}
              <div className="border-t border-border pt-8 mt-8">
                <p className="text-sm text-muted-foreground mb-6">
                  Join the waitlist to be notified when membership launches
                </p>
                <form onSubmit={handleWaitlist} className="space-y-4">
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="rounded-none border-border"
                    data-testid="waitlist-name"
                  />
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="rounded-none border-border"
                    data-testid="waitlist-email"
                  />
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-none bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-xs tracking-[0.2em] uppercase"
                    data-testid="join-waitlist-btn"
                  >
                    {isSubmitting ? "Joining..." : "Join the Waitlist"}
                  </Button>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary" data-testid="membership-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="heading-lg mb-6 text-primary-foreground">
              Join the Sanctuary
            </h2>
            <p className="body-text text-primary-foreground/80 mb-10">
              Begin your journey back to yourself.
            </p>
            <p className="text-sm text-primary-foreground/60 italic">
              Membership launching soon. Join the waitlist above to be the first to know.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
